import { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { IoClose } from 'react-icons/io5';

import { MixpanelEvents, ESdkEvents } from '../../contracts';
import { sendFEEvent } from '../../eventUtil';
import { Description } from './components/Description/Description';
import { useCheckout } from '../../hooks/use-checkout';
import { PendingMessage } from './components/PendingMessage';
import { ErrorMessage } from './components/ErrorMessage';
import { SuccessMessage } from './components/SuccessMessage';
import { useDeviceHelper } from '../../hooks/use-device-helper';
import { useAppConfig } from '../../hooks/use-app-config';
import { PaymentForm } from './components/PaymentForm/PaymentForm';
import { ExpressCheckout } from './components/ExpressCheckout/ExpressCheckout';
import { ScreenLock } from './components/ScreenLock/ScreenLock';
import { usePaymentState } from './hooks/use-payment-state';
import { useAPI } from '../../services/api.service';
import { ECheckoutStatus, RedirectStatus } from '../../constants/enums';
import { useTranslation } from 'react-i18next';
import { mobileSDKUtils } from '../../utils/mobile-sdk-utils';
import { getRedirectUrlWithEventParams } from '../../utils/url-utils';
import { useCheckoutFlowType } from './hooks/use-checkout-flow-type';
import { useResizeObserver } from './hooks/use-resize-observer';
import { getIsSupercellTemplate } from '../../utils/get-is-supercell-template';

import './styles.scss';
import { sendMixpanelEvent } from '../../utils/mixpanel-utils';

const IS_SUPERCELL_TEMPLATE = getIsSupercellTemplate();

interface CheckoutProps {
  onClose: () => void;
}

export default function Checkout({ onClose }: CheckoutProps) {
  const [showInvalidZipMode, setShowInvalidZipMode] = useState(false);
  const { isPaymentLinksFlow } = useCheckoutFlowType();
  const { isMobile, getIosVersion, isOldIos } = useDeviceHelper();
  const { theme, featureFlags } = useAppConfig();
  const { getEventParams, redirectUrl, disableRedirect, getMixpanelEventParams } = useCheckout();
  const [isScreenLock, setIsScreenLock] = useState<boolean>(false);
  const [apmTab, setApmTab] = useState<any>();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { API } = useAPI();
  const checkoutStatus = usePaymentState((state) => state.checkoutStatus);
  const isPaymentElementLoading = usePaymentState((state) => state.isPaymentElementLoading);
  const isExpressCheckoutLoading = usePaymentState((state) => state.isExpressCheckoutLoading);
  const isPaymentLoading = usePaymentState((state) => state.isPaymentLoading);
  const { paymentRef, descriptionRef } = useResizeObserver();
  const showMobileOpenCloseAnimation = Boolean(
    isMobile && (featureFlags?.checkout_open_close_animation || mobileSDKUtils.isMobileSdk()),
  );
  const hideSuccessScreen = Boolean(
    featureFlags?.checkout_disable_success_screen &&
    checkoutStatus === ECheckoutStatus.SUCCESS_SHOW_PENDING,
  );

  useEffect(() => {
    if (hideSuccessScreen) {
      if (showMobileOpenCloseAnimation) {
        onClose();
      } else {
        sendFEEvent(ESdkEvents.CLOSE_CHECKOUT, {
          ...getEventParams(),
        });
        sendMixpanelEvent(MixpanelEvents.CHECKOUT_CLOSED, { ...getMixpanelEventParams() });
      }
    }
  }, [hideSuccessScreen, onClose, showMobileOpenCloseAnimation]);

  const handleClose = async () => {
    if (checkoutStatus === ECheckoutStatus.INIT) {
      try {
        await API.cancelOrder();
      } catch (error) {
        console.error(error);
      }
    }
    if (showMobileOpenCloseAnimation) {
      onClose();
    } else {
      sendFEEvent(ESdkEvents.CLOSE_CHECKOUT, {
        ...getEventParams(),
      });
    }
    sendMixpanelEvent(MixpanelEvents.CHECKOUT_CLOSED, { ...getMixpanelEventParams() });
  };

  const getMessage = () => {
    if (
      checkoutStatus === ECheckoutStatus.PENDING ||
      checkoutStatus === ECheckoutStatus.SUCCESS_SHOW_PENDING
    ) {
      return <PendingMessage />;
    }

    if (checkoutStatus === ECheckoutStatus.ERROR) {
      return (
        <ErrorMessage
          handleClose={() => {
            if (redirectUrl && !disableRedirect) {
              window.location.href = getRedirectUrlWithEventParams(
                redirectUrl,
                RedirectStatus.FAIL,
                getEventParams(),
              );
            } else {
              handleClose();
            }
          }}
        />
      );
    }

    if (checkoutStatus === ECheckoutStatus.SUCCESS) {
      return (
        <SuccessMessage
          handleClose={() => {
            if (redirectUrl && !disableRedirect) {
              window.location.href = getRedirectUrlWithEventParams(
                redirectUrl,
                RedirectStatus.SUCCESS,
                getEventParams(),
              );
            } else {
              handleClose();
            }
          }}
        />
      );
    }
    return null;
  };

  const removeOverlay = () => {
    setIsScreenLock(false);
    if (apmTab) apmTab.close();
    // Signal any in-flight Stripe APM polling loop to exit, and clear the
    // pay-button loading state immediately. Without this the polling loop in
    // useStripeSubmit would keep running (potentially forever under COOP, where
    // `apmTab.closed` is unreliable), leaving the checkout stuck.
    usePaymentState.getState().abortApmFlow();
  };

  const showCloseButton = !(
    featureFlags?.checkout_deeplinks_disable_store ||
    isPaymentLoading ||
    IS_SUPERCELL_TEMPLATE
  );

  // Fatal boot errors are handled by ErrorBoundary via AppConfigProvider

  return (
    <>
      {getMessage()}
      <Box
        className={`checkout-container ${
          isOldIos(getIosVersion()) ? 'position-fixed-not-supported-patch' : ''
        } ${mobileSDKUtils.isMobileSdk() ? 'mobile-sdk' : ''}
        ${IS_SUPERCELL_TEMPLATE ? 'supercell-template' : ''}`}
        display={checkoutStatus === ECheckoutStatus.INIT ? 'flex' : 'none'}
      >
        {showCloseButton && (
          <div
            className="close-button"
            onClick={async () => {
              if (redirectUrl && !disableRedirect) {
                await API.cancelOrder().finally(() => {
                  window.location.href = getRedirectUrlWithEventParams(
                    redirectUrl,
                    RedirectStatus.CANCEL,
                    getEventParams(),
                  );
                });
              } else {
                handleClose();
              }
            }}
            style={{ color: isMobile ? theme?.text : 'black' }}
          >
            <IoClose />
          </div>
        )}
        <Description ref={descriptionRef as any} />
        <Box className="payment" ref={paymentRef}>
          {/* {!isPaymentElementLoading && ( // old code here to see that there is no regression after payment link UX improvements */}
          {(isPaymentLinksFlow ? true : !isPaymentElementLoading) && (
            <Box
              className={`express-checkout-container 
                ${isPaymentLinksFlow ? 'payment-links' : ''}
                ${isExpressCheckoutLoading ? '' : 'show'}`}
            >
              <ExpressCheckout
                setOverlay={setIsScreenLock}
                onDisabledButtonClicked={() => setShowInvalidZipMode(true)}
              />
            </Box>
          )}
          <PaymentForm
            setOverlay={setIsScreenLock}
            setApmTab={setApmTab}
            onZipCodeModeBackClicked={() => setShowInvalidZipMode(false)}
            showInvalidZipMode={showInvalidZipMode}
          />
        </Box>
      </Box>
      {isScreenLock && (
        <ScreenLock
          action={removeOverlay}
          actionText={t('clickHereToTryAgain')}
          text={t('dontSeeSecureBrowserTab')}
        />
      )}
    </>
  );
}
